import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import spinnerIcon from "././../assets/img/loadingSpinner.gif";
import { useAuth0 } from "@auth0/auth0-react";
import { apiGet } from "../services/AuthService.js";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(stripePublishableKey);

const DEFAULT_BILLING_PERIOD = 1; // 1 = yearly, 2 = monthly (legacy convention)

const LEGACY_RETURN_PATH = "/membership";
const NEW_RETURN_PATH = "/membership-plans";

// Persist the flow locally; Stripe return URL can be malformed (double '?') or omit accountId.
const CHECKOUT_FLOW_STORAGE_KEY = "totdata_checkout_flow"; // "legacy" | "new"
const POST_CHECKOUT_RETURN_STORAGE_KEY = "totdata_post_checkout_return";

const isSafeSpaPath = (value) => {
    const textValue = String(value || "").trim();
    if (!textValue.startsWith("/")) return false;
    if (textValue.startsWith("//") || /^\w+:\/\//i.test(textValue)) return false;
    return true;
};

const extractFirstMatch = (input, regex) => {
    const text = String(input || "");
    const match = text.match(regex);
    return match && match[1] ? match[1] : "";
};

const parseStripeReturnInfo = (routerSearch, routerHash) => {
    const search = String(routerSearch || "");
    const hash = String(routerHash || "");

    // Prefer window.location.href for resilience against malformed query strings.
    const href = typeof window !== "undefined" ? String(window.location.href || "") : "";

    // 1) session_id (Stripe uses session_id=...; in malformed cases it can contain "?accountId=...").
    const sessionId = extractFirstMatch(href, /(?:\?|&|#)session_id=([^&#]+)/i) || extractFirstMatch(search, /(?:\?|&)session_id=([^&]+)/i);

    // 2) accountId can be lost by URLSearchParams when return URL has multiple '?', so regex scan the full href.
    const accountIdRaw = extractFirstMatch(href, /(?:\?|&|#)accountId=(\d+)/i) || extractFirstMatch(search, /(?:\?|&)accountId=(\d+)/i) || extractFirstMatch(hash, /(?:\?|&|#)accountId=(\d+)/i);

    // 3) flow marker (recommended server-side: flow=new).
    const flowRaw = extractFirstMatch(href, /(?:\?|&|#)flow=([^&#]+)/i) || extractFirstMatch(search, /(?:\?|&)flow=([^&]+)/i) || extractFirstMatch(hash, /(?:\?|&|#)flow=([^&#]+)/i);

    const accountId = Number.isFinite(Number(accountIdRaw)) ? Number(accountIdRaw) : 0;
    const flow = String(flowRaw || "").trim().toLowerCase();

    // If session_id was captured with extra junk, keep only the Stripe session prefix portion (cs_...).
    const normalizedSessionId = sessionId ? String(sessionId).split("?")[0].trim() : "";

    return { sessionId: normalizedSessionId, accountId, flow };
};

const normalizeItems = (items) => {
    if (!items) return null;

    if (typeof items === "string") {
        try {
            const parsed = JSON.parse(items);
            if (parsed && typeof parsed === "object") return parsed;
        } catch {
            return null;
        }
    }

    if (typeof items === "object") return items;

    return null;
};

const buildNewCheckoutSessionUrl = (accountId, billingPeriod, items) => {
    const params = new URLSearchParams();
    params.set("accountId", String(accountId));
    params.set("billingPeriod", String(billingPeriod));

    Object.entries(items || {}).forEach(([priceId, qty]) => {
        const normalizedPriceId = String(priceId || "").trim();
        const normalizedQty = String(qty ?? "").trim();
        if (!normalizedPriceId || !normalizedQty) return;
        params.append(`items[${normalizedPriceId}]`, normalizedQty);
    });

    return `a_payments/create-checkout-session-new?${params.toString()}`;
};

const Payment = () => {
    const { getAccessTokenSilently } = useAuth0();

    const outletValues = useOutletContext();
    const currentAccount = outletValues?.[0] ?? null;

    const navigate = useNavigate();
    const location = useLocation();

    const [pageStatus, setPageStatus] = useState("loading");
    const [pageErrors, setPageErrors] = useState("");

    const [clientSecret, setClientSecret] = useState("");
    const [options, setOptions] = useState(null);

    const returnInfo = useMemo(() => parseStripeReturnInfo(location.search, location.hash), [location.search, location.hash]);

    useEffect(() => {
        (async () => {
            try {
                const accountId = currentAccount?.id || 0;

                // Stripe return: route back to the correct membership page, then let that page finalize/refresh.
                if (returnInfo.sessionId) {
                    const storedFlow = String(sessionStorage.getItem(CHECKOUT_FLOW_STORAGE_KEY) || "").trim().toLowerCase();
                    const storedReturnPath = String(sessionStorage.getItem(POST_CHECKOUT_RETURN_STORAGE_KEY) || "").trim();

                    // Route to new UI only when explicitly indicated; default to legacy.
                    const isNewFlow = returnInfo.flow === "new" || storedFlow === "new";

                    sessionStorage.removeItem(CHECKOUT_FLOW_STORAGE_KEY);
                    sessionStorage.removeItem(POST_CHECKOUT_RETURN_STORAGE_KEY);

                    const destinationPath = isSafeSpaPath(storedReturnPath) ? storedReturnPath : (isNewFlow ? NEW_RETURN_PATH : LEGACY_RETURN_PATH);

                    navigate(destinationPath, {
                        replace: true,
                        state: {
                            checkoutSessionId: returnInfo.sessionId,
                            useNewCheckout: isNewFlow,
                        },
                    });
                    return;
                }

                // Starting checkout
                if (!accountId) {
                    setPageStatus("loading");
                    return;
                }

                const billingPeriod = location.state?.billingPeriod ?? DEFAULT_BILLING_PERIOD;
                const requestedItems = normalizeItems(location.state?.items ?? null);

                const shouldUseNewCheckout = Boolean(location.state?.useNewCheckout) || (requestedItems && Object.keys(requestedItems).length > 0);

                // Persist flow for Stripe return routing (covers missing accountId or malformed query parsing).
                sessionStorage.setItem(CHECKOUT_FLOW_STORAGE_KEY, shouldUseNewCheckout ? "new" : "legacy");
                sessionStorage.setItem(POST_CHECKOUT_RETURN_STORAGE_KEY, shouldUseNewCheckout ? NEW_RETURN_PATH : LEGACY_RETURN_PATH);

                if (shouldUseNewCheckout && (!requestedItems || Object.keys(requestedItems).length === 0)) {
                    setPageStatus("error");
                    setPageErrors("No Stripe items were provided for the new checkout flow.");
                    return;
                }

                const result = shouldUseNewCheckout
                    ? await createCheckoutSessionNew(accountId, billingPeriod, requestedItems, getAccessTokenSilently)
                    : await createCheckoutSessionLegacy(accountId, billingPeriod, getAccessTokenSilently);

                if (!result?.success || !result?.data?.clientSecret) {
                    setPageStatus("error");
                    setPageErrors("Failed to start checkout.");
                    return;
                }

                const secret = result.data.clientSecret;
                setClientSecret(secret);
                setOptions({ clientSecret: secret });
                setPageStatus("ready");
            } catch (ex) {
                setPageStatus("error");
                setPageErrors(ex?.message ?? "Unknown error.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAccount?.id, location.search, location.hash]);

    const showPageLoading = () => (
        <div>
            <img src={spinnerIcon} alt="loading..." />
        </div>
    );

    const showPageError = () => <div>Oops, there was a page load error: {pageErrors}</div>;

    const showPayment = () => (
        <div id="checkout">
            {clientSecret && options && (
                <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                    <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
            )}
        </div>
    );

    return (
        <div>
            <h1>Payment</h1>
            <div><br /></div>

            {(() => {
                switch (pageStatus) {
                    case "loading":
                        return showPageLoading();
                    case "ready":
                        return showPayment();
                    case "error":
                        return showPageError();
                    default:
                        return <p>No Page Loaded.</p>;
                }
            })()}

            <br />
        </div>
    );
};

const createCheckoutSessionLegacy = async (accountId, billingPeriod, getAccessTokenSilently) => {
    const apiUrl = `a_payments/create-checkout-session?accountId=${accountId}&billingPeriod=${billingPeriod}`;
    const response = await apiGet(apiUrl, getAccessTokenSilently);
    const json = await response.json();
    return { success: response.ok, data: json };
};

const createCheckoutSessionNew = async (accountId, billingPeriod, items, getAccessTokenSilently) => {
    const apiUrl = buildNewCheckoutSessionUrl(accountId, billingPeriod, items);
    const response = await apiGet(apiUrl, getAccessTokenSilently);
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(text || "Unexpected error from server.");
    }
    const json = await response.json();
    return { success: response.ok, data: json };
};

export default Payment;
